// src/pages/parametrizacao/LogoEmailPanel.tsx
//
// Gerencia o logo de email do cliente (Phase 1 emails Bluelt).
// Endpoints sob /v1/cliente-imagens-icon: GET (bytes PNG), POST (multipart) e DELETE.
// Backend resolve tenant pelo subdominio e normaliza para 240x80 PNG.
//
// Permissoes:
//   - visualizar: configuration.listar (gate ja aplicado no botao em Parametrizacao.tsx)
//   - upload/delete: configuration.editar (ROLE_MASTER bypassa via hasPermission)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiGet, apiDelete } from '@/services/api';
import { getEnvironmentConfig } from '@/services/environment';
import { useAuth } from '@/contexts/AuthContext';
import { showAlert, showConfirm } from '@/contexts/DialogContext';
import { Toast } from '@/contexts/ToastContext';
import DeleteButton from '@/components/common/DeleteButton';
import UploadButton from '@/components/common/UploadButton';
import './LogoEmailPanel.css';

interface LogoEmailPanelProps {
  theme: 'dark' | 'light';
}

const MIME_ACEITOS = ['image/png', 'image/jpeg'] as const;
const TAMANHO_MAX_BYTES = 2 * 1024 * 1024;
const ENDPOINT = '/v1/cliente-imagens-icon';

const LogoEmailPanel: React.FC<LogoEmailPanelProps> = ({ theme }) => {
  const { hasPermission } = useAuth();
  const podeEditar = hasPermission('configuration.editar');

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const aplicarBlob = useCallback((blob: Blob | null) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (blob) {
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setLogoUrl(url);
    } else {
      setLogoUrl(null);
    }
  }, []);

  const carregarLogo = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiGet(ENDPOINT, { responseType: 'blob' });
      aplicarBlob(res.data as Blob);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        aplicarBlob(null);
        return;
      }
      aplicarBlob(null);
      await showAlert('Erro ao carregar logo de e-mail.', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [aplicarBlob]);

  useEffect(() => {
    carregarLogo();
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [carregarLogo]);

  const handleSelecionarArquivo = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!MIME_ACEITOS.includes(file.type as typeof MIME_ACEITOS[number])) {
      Toast.error('Formato nao suportado. Aceitos: PNG ou JPEG.');
      return;
    }
    if (file.size > TAMANHO_MAX_BYTES) {
      Toast.error('Arquivo excede o limite de 2 MB.');
      return;
    }

    setIsUploading(true);
    try {
      // POST via fetch puro: o `apiPost` injeta `Content-Type: application/json` do
      // default global do axios e o backend rejeita o multipart. fetch nao seta
      // Content-Type quando o body e FormData — o browser preenche com boundary.
      const formData = new FormData();
      formData.append('arquivo', file);
      const { BASE_URL_BACKEND } = getEnvironmentConfig();
      const token = sessionStorage.getItem('jwtToken') || '';
      const res = await fetch(`${BASE_URL_BACKEND}${ENDPOINT}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        let mensagem = 'Erro ao enviar logo.';
        try {
          const body = await res.json();
          if (body?.error) mensagem = body.error;
        } catch {
          // resposta sem JSON valido — usa fallback
        }
        await showAlert(mensagem, { variant: 'error' });
        return;
      }
      Toast.success('Logo atualizado.');
      await carregarLogo();
    } catch {
      await showAlert('Erro ao enviar logo.', { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    const confirmado = await showConfirm('Deseja remover o logo de e-mail?', {
      variant: 'warning',
      title: 'Confirmar exclusao',
    });
    if (!confirmado) return;

    setIsDeleting(true);
    try {
      await apiDelete(ENDPOINT);
      aplicarBlob(null);
      Toast.success('Logo removido.');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao remover logo.', { variant: 'error' });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`logo-email-panel ${theme}`}>
      <p className="logo-email-hint">
        Proporcao ideal: 3:1. Formato PNG ou JPEG, com até 2 MB. Normalização 240x80.
      </p>

      <div className="logo-email-content">
        {isLoading ? (
          <p>Carregando logo...</p>
        ) : logoUrl ? (
          <img src={logoUrl} alt="Logo de e-mail do cliente" className="logo-email-img" />
        ) : (
          <p className="logo-email-empty">Nenhum logo configurado.</p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className="logo-email-actions">
          <UploadButton
            isLoading={isUploading}
            onClick={handleSelecionarArquivo}
            disabled={!podeEditar || isUploading || isDeleting}
            label="Enviar"
          />
          {logoUrl && (
            <DeleteButton
              onClick={handleDelete}
              disabled={!podeEditar || isUploading || isDeleting}
            />
          )}
        </div>

        {!podeEditar && (
          <p className="logo-email-hint">Voce nao tem permissao para alterar o logo.</p>
        )}
      </div>
    </div>
  );
};

export default LogoEmailPanel;
